import {
  Box,
  Button,
  Card,
  CardBody,
  Center,
  Flex,
  FormLabel,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  StackDivider,
  Switch,
  Text,
  useToast,
  VStack,
  Badge,
} from "@chakra-ui/react";
import * as Yup from "yup";
import { CiTimer, CiSearch } from "react-icons/ci";
import { FaBan, FaCheck, FaFileAlt, FaTimes } from "react-icons/fa";
import { CopyIcon } from "@chakra-ui/icons";
import { useEffect, useState } from "react";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { cepFormatter } from "@/utils/cepFormatter";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { MdDescription } from "react-icons/md";
import { Form, Formik } from "formik";
import { formatPhone } from "@/utils/formatPhone";
import { ProposalStatus, getStatusProps } from "../../../utils/consignado/proposalStatusRenderer";
import { getProposalStatusAndCustomStatusConsignado } from "../../../utils/consignado/getProposalStatusAndCustomStatus";
import TextInputField from "../../TextInputField";
import { aprovarPropostaConsignado, getConsignadoProposals, rejectProposalConsignado, updateProposalAnaliseBank } from "../../../services/consignado/proposal.service";
import { IConsignadoProposal, IConsignadoProposalsList, ProposalConsignadoCustomStatus } from "../../../models/consignado/proposal.model";
import { BrlCurrencyFormatter } from "../../../utils/BrlCurrencyFormatter";
import FilePreview from "../../FilePreview";
import PdfPreview from "../../PdfPreview/PdfPreview";
import CreditAuctionProposalsPagination from "../../CreditAuctionProposalsPage/credit-auction-proposals-pagination";
import { tagButtonStyle } from "../../FinancialShared/tableStyles";
import ScriptContratacaoModal from "./ScriptContratacaoModal";
import { getOfertaEscolhida } from "@/utils/consignado/ofertaEscolhida";
import { useQueryClient } from "@tanstack/react-query";
import { differenceInMonths } from "date-fns";

// Formata um total de meses como "X anos e Y meses" (trata plural e < 1 mês).
function formatYearsMonths(totalMonths?: number | null): string | null {
  if (totalMonths == null || totalMonths < 0) return null;
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? "ano" : "anos"}`);
  if (months > 0) parts.push(`${months} ${months === 1 ? "mês" : "meses"}`);
  return parts.length ? parts.join(" e ") : "menos de 1 mês";
}

// Tempo de vínculo da data de admissão (DD/MM/AAAA) até hoje. Mesma contagem
// (differenceInMonths) do "Tempo de vínculo" da análise de crédito. Não é
// cálculo financeiro — só formatação de período.
function formatAdmissionTenure(dateBR?: string | null): string | null {
  if (!dateBR) return null;
  const [day, month, year] = dateBR.split("/").map(Number);
  if (!day || !month || !year) return null;
  const admission = new Date(year, month - 1, day);
  if (isNaN(admission.getTime())) return null;
  return formatYearsMonths(differenceInMonths(new Date(), admission));
}

// Campo somente-leitura no padrão do card "Revisar Proposta": rótulo pequeno
// acima e valor em caixa cinza. Mostra "-" quando o back não enviou o dado.
function ReadOnlyField({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  return (
    <Box>
      <Text fontSize="10px" color="gray.600" mb={1} fontFamily="Lexend Deca">
        {label}
      </Text>
      <Box bg="gray.100" borderRadius="md" px={2} py={1}>
        <Text
          fontSize="12px"
          height="20px"
          color="gray.800"
          fontFamily="Lexend Deca"
          noOfLines={1}
          title={value || undefined}
        >
          {value || "-"}
        </Text>
      </Box>
    </Box>
  );
}

export default function BancoAnalise() {
  const backgroundStyle = useConditionalBackground();
  const toast = useToast();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [proposals, setProposals] = useState<IConsignadoProposalsList>();
  const [visibleInput, setVisibleInput] = useState<string | undefined>(
    undefined
  );
  const [scriptProposal, setScriptProposal] = useState<
    IConsignadoProposal | undefined
  >(undefined);
  const queryClient = useQueryClient()

  const stripDateTimePrefix = (message: string): string => {
    if (!message) return "";
    return message.replace(/^[0-9]{2}\/[0-9]{2}\/[0-9]{2}\s+[0-9]{2}:[0-9]{2}: ?/, "");
  };

  const getStatusBadgeText = (proposal: IConsignadoProposal): string =>
    stripDateTimePrefix(
      getProposalStatusAndCustomStatusConsignado(
        ProposalStatus[proposal.status as keyof typeof ProposalStatus],
        proposal?.custom_status! as string
      )
    );

  // statusMap guarda tokens como "yellow.500" — o prefixo é o colorScheme.
  const getStatusColorScheme = (status: string): string =>
    (getStatusProps(status)?.color ?? "gray").split(".")[0];

  // Copia o valor cru (sem máscara), mais útil pra colar em outros sistemas.
  const copyFieldToClipboard = async (value?: string | null) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast({
        title: "Copiado",
        description: value,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error copying field:", error);
      toast({
        title: "Erro",
        description: "Erro ao copiar",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSwitchChange = (cardId: string) => {
    setVisibleInput((prev) => (prev === cardId ? undefined : cardId));
  };
 
  const loadProposals = async () => {
    try {
      const data = await getConsignadoProposals({
        pagina: page,
        tamanho_pagina: pageSize,
        status: ProposalStatus.PENDING_BANK_ANALYSIS,
        search_term: search,
        incluir: "banco_detalhe"
      });
      setProposals(data);
      // Aprovar/rejeitar o último item de uma página pode deixá-la vazia
      if (!data.data?.length && data.pagina_atual > 1) {
        setPage(data.pagina_anterior ?? 1);
      }
    } catch (error) {
      console.error("Error loading proposals:", error);
    }
  };

  const handleGetCustomStatus = (customStatus: string) => {
    const customStatusParsed: ProposalConsignadoCustomStatus = customStatus ? JSON.parse(customStatus) : ''
    const message = customStatusParsed.acoes?.analise_banco?.mensagem
    return message ?? ""
  }

  const approveProsal = async (proposalId: string) => {
    try {
      await aprovarPropostaConsignado(proposalId);
      queryClient.invalidateQueries({ queryKey: ['proposal_pending_bank_analysis'] })
      toast({
        title: "Proposta aprovada",
        description: "A proposta foi aprovada com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      loadProposals();
    } catch (error) {
      console.error("Error approving proposal:", error);
      toast({
        title: "Erro",
        description: "Erro ao aprovar proposta",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const cancelProposal = async (proposalId: string, isBlackListed = false) => {
    try {
      await rejectProposalConsignado(proposalId, isBlackListed);
      queryClient.invalidateQueries({ queryKey: ['proposal_pending_bank_analysis'] })
      toast({
        title: "Proposta rejeitada",
        description: "A proposta foi rejeitada com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      loadProposals();
    } catch (error) {
      console.error("Error canceling proposal:", error);
      toast({
        title: "Erro",
        description: "Erro ao rejeitar proposta",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

 
  const atualizarStatusProposta = async (proposalId: string, customStatusMsg: string) => {
    try {
      // console.log({proposalId, status})
      // tirar o log
      await updateProposalAnaliseBank(proposalId, customStatusMsg);
      toast({
        title: "Proposta atualizada",
        description: "A proposta foi atualizada com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      loadProposals();
    } catch (error) {
      console.error("Error updating proposal status:", error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar proposta",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const validationSchema = Yup.object().shape({
    status: Yup.string()
      .min(3, "O status deve ter no mínimo 3 caracteres")
      .required("O status é obrigatório"),
  });

  useEffect(() => {
    loadProposals();
  }, [search, page, pageSize]);

  // function getPixTypeLabel(value?: string): string {
  //   switch (value) {
  //     case "EMAIL":
  //       return "Email";
  //     case "PHONE":
  //       return "Celular";
  //     case "DOCUMENT":
  //       return "CPF";
  //     default:
  //       return "Desconhecido";
  //   }
  // }

  return (
    <Box w="100%" display="flex" flexDirection="column" p={{ base: 2, md: 4 }}>
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        color="white"
        boxShadow="md"
        w="100%"
      >
        <Flex
          flexDirection={{ base: "column", md: "row" }}
          alignItems={{ base: "flex-start", md: "center" }}
          justifyContent="space-between"
          mb={4}
        >
          <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
            <CiTimer size="2rem" />
            <Text
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              ml={4}
              fontFamily="Lexend Deca"
            >
              Análise Banco
            </Text>
          </Flex>
        </Flex>
        <Flex mt={{ base: 4, md: 0 }} justifyContent="flex-start">
          <InputGroup width={{ base: "100%", md: "30%" }}>
            <InputLeftElement mt={5} pointerEvents="none">
              <CiSearch color="black" size="2rem" />
            </InputLeftElement>
            <Input
              mt={4}
              placeholder="Pesquisar..."
              variant="filled"
              height={{ base: "56px", md: "48px" }}
              type="text"
              bg="white"
              maxLength={30}
              value={search}
              onChange={(ev) => {
                setSearch(ev.target.value);
                setPage(1);
              }}
              color="black"
              _focus={{ bg: "white" }}
              _hover={{ bg: "white" }}
              fontFamily="Lexend Deca"
            />
          </InputGroup>
        </Flex>
      </Box>
      <CreditAuctionProposalsPagination
        currentPage={proposals?.pagina_atual ?? page}
        totalPages={proposals?.total_paginas ?? 1}
        pageSize={pageSize}
        totalItems={proposals?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        mb={0}
      />
      <Flex wrap="wrap" w="100%" gap={4} mt={4}>
        {proposals?.data?.length === 0 && (
          <Center w="100%">
            <Heading mt={100} color="gray.600">
              Não há propostas em análise bancária
            </Heading>
          </Center>
        )}
        {proposals?.data?.map((proposal, index) => (
          <Card key={index} w="100%">
            <CardBody display="flex" flexDirection="column" w="100%">
              <Stack divider={<StackDivider />} spacing="4" w="100%">
                <Box>
                  <Flex
                    direction={{ base: "column", md: "row" }}
                    gap={4}
                    overflow="auto"
                  >
                    <Flex justify="flex-start" w={{ base: "100%", md: "30%" }}>
                      <Box
                        p={{ base: 4, md: 4 }}
                        borderWidth="1px"
                        borderRadius="md"
                        boxShadow="base"
                        bg="white"
                        w={{ base: "100%", md: "100%" }}
                      >
                        <Flex direction="column" align="center" mb={2} gap={2}>
                          <Flex align="center">
                            <Icon
                              as={MdDescription}
                              boxSize={{ base: 5, md: 5 }}
                              color="gray.500"
                              mr={2}
                            />
                            <Heading
                              size={{ base: "md", md: "md" }}
                              color="gray.700"
                              fontFamily="Lexend Deca"
                            >
                              Revisar Proposta
                            </Heading>
                          </Flex>
                          <Badge
                            colorScheme={getStatusColorScheme(proposal.status)}
                            px={2}
                            py={1}
                            fontSize="xs"
                            fontWeight="normal"
                            fontFamily="Lexend Deca"
                            borderRadius="md"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            maxW="100%"
                            title={getStatusBadgeText(proposal)}
                          >
                            {getStatusBadgeText(proposal)}
                          </Badge>
                        </Flex>
                        <VStack spacing={2} align="stretch">
                          <Box>
                            <Text
                              fontSize="10px"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              Número da Proposta
                            </Text>
                            <Flex
                              bg="gray.100"
                              borderRadius="md"
                              px={2}
                              py={1}
                              align="center"
                              justify="space-between"
                              gap={1}
                            >
                              <Text
                                fontSize="12px"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {proposal.short_hash}
                              </Text>
                              <IconButton
                                aria-label="Copiar número da proposta"
                                icon={<CopyIcon />}
                                size="xs"
                                variant="ghost"
                                color="gray.500"
                                h="20px"
                                minW="20px"
                                onClick={() =>
                                  copyFieldToClipboard(proposal.short_hash)
                                }
                              />
                            </Flex>
                          </Box>
                          <Box>
                            <Text
                              fontSize="10px"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              Nome
                            </Text>
                            <Flex
                              bg="gray.100"
                              borderRadius="md"
                              px={2}
                              py={1}
                              align="center"
                              justify="space-between"
                              gap={1}
                            >
                              <Text
                                fontSize="12px"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {proposal.nome_cliente}
                              </Text>
                              <IconButton
                                aria-label="Copiar nome"
                                icon={<CopyIcon />}
                                size="xs"
                                variant="ghost"
                                color="gray.500"
                                h="20px"
                                minW="20px"
                                onClick={() =>
                                  copyFieldToClipboard(proposal.nome_cliente)
                                }
                              />
                            </Flex>
                          </Box>
                          <Box>
                            <Text
                              fontSize="10px"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              CPF
                            </Text>
                            <Flex
                              bg="gray.100"
                              borderRadius="md"
                              px={2}
                              py={1}
                              align="center"
                              justify="space-between"
                              gap={1}
                            >
                              <Text
                                fontSize="12px"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {cpfFormatter(
                                  proposal?.cpf
                                )}
                              </Text>
                              <IconButton
                                aria-label="Copiar CPF"
                                icon={<CopyIcon />}
                                size="xs"
                                variant="ghost"
                                color="gray.500"
                                h="20px"
                                minW="20px"
                                onClick={() => copyFieldToClipboard(proposal.cpf)}
                              />
                            </Flex>
                          </Box>
                          <Box>
                            <Text
                              fontSize="10px"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              CEP
                            </Text>
                            <Box bg="gray.100" borderRadius="md" px={2} py={1}>
                              <Text
                                fontSize="12px"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {cepFormatter(
                                  proposal.cep
                                )}
                              </Text>
                            </Box>
                          </Box>
                          {/* "Dados Bancários" (agência) e "Banco que vai
                              bloquear o saldo" (BMP) ocultados a pedido. */}
                          <Box>
                            <Text
                              fontSize="10px"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              Telefone
                            </Text>
                            <Flex
                              bg="gray.100"
                              borderRadius="md"
                              px={2}
                              py={1}
                              align="center"
                              justify="space-between"
                              gap={1}
                            >
                              <Text
                                fontSize="12px"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {formatPhone(
                                  proposal.telefone || ""
                                )}
                              </Text>
                              <IconButton
                                aria-label="Copiar telefone"
                                icon={<CopyIcon />}
                                size="xs"
                                variant="ghost"
                                color="gray.500"
                                h="20px"
                                minW="20px"
                                onClick={() =>
                                  copyFieldToClipboard(proposal.telefone)
                                }
                              />
                            </Flex>
                          </Box>
                          <Box>
                            <Text
                              fontSize="10px"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              Valor Liberado
                            </Text>
                            <Box bg="gray.100" borderRadius="md" px={2} py={1}>
                              <Text
                                fontSize="12px"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {BrlCurrencyFormatter(
                                  getOfertaEscolhida(proposal.custom_status)
                                    .oferta?.releasedAmount
                                )}
                              </Text>
                            </Box>
                          </Box>
                          <Box>
                            <Text
                              fontSize="10px"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              Score Unico
                            </Text>
                            <Box bg="gray.100" borderRadius="md" px={2} py={1}>
                              <Text
                                fontSize="12px"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {proposal.unico_score}
                              </Text>
                            </Box>
                          </Box>
                          <Box>
                            <Text
                              fontSize="10px"
                              color="gray.600"
                              mb={1}
                              fontFamily="Lexend Deca"
                            >
                              Corban
                            </Text>
                            <Box bg="gray.100" borderRadius="md" px={2} py={1}>
                              <Text
                                fontSize="12px"
                                height="20px"
                                color="gray.800"
                                fontFamily="Lexend Deca"
                                noOfLines={1}
                              >
                                {proposal.nome_corban || "-"}
                              </Text>
                            </Box>
                          </Box>
                          <ReadOnlyField
                            label="Data de Admissão"
                            value={
                              proposal.data_admissao
                                ? `${proposal.data_admissao}${
                                    formatAdmissionTenure(proposal.data_admissao)
                                      ? ` (${formatAdmissionTenure(
                                          proposal.data_admissao
                                        )})`
                                      : ""
                                  }`
                                : null
                            }
                          />
                          <ReadOnlyField
                            label="Idade do CNPJ"
                            value={
                              proposal.idade_cnpj_meses != null
                                ? `${proposal.idade_cnpj_meses} ${
                                    proposal.idade_cnpj_meses === 1
                                      ? "mês"
                                      : "meses"
                                  }${
                                    proposal.idade_cnpj_meses >= 12
                                      ? ` (${formatYearsMonths(
                                          proposal.idade_cnpj_meses
                                        )})`
                                      : ""
                                  }`
                                : null
                            }
                          />
                          <ReadOnlyField
                            label="Setor (CNAE) do Empregador"
                            value={
                              proposal.setor_cnae
                                ? proposal.codigo_cnae
                                  ? `${proposal.codigo_cnae} - ${proposal.setor_cnae}`
                                  : proposal.setor_cnae
                                : null
                            }
                          />
                          <ReadOnlyField
                            label="Grupo Ocupacional (CBO)"
                            value={
                              proposal.descricao_cbo
                                ? proposal.codigo_cbo
                                  ? `${proposal.codigo_cbo} - ${proposal.descricao_cbo}`
                                  : proposal.descricao_cbo
                                : null
                            }
                          />
                          <ReadOnlyField
                            label="Base de Cálculo da Margem"
                            value={
                              proposal.base_calculo_margem != null
                                ? BrlCurrencyFormatter(
                                    proposal.base_calculo_margem
                                  )
                                : null
                            }
                          />
                          {/* Salário (totalEarnings) ocultado: diverge entre os
                              motores (risco x VAAS x BMP) e do valor ao vivo da
                              margem. Back continua enviando `salario`. */}
                        </VStack>
                      </Box>
                    </Flex>
                    <Box w={{ base: "100%", md: "23%" }} h="60vh">
                      <FilePreview
                        fileUrl={proposal.frente_documento!}
                      />
                    </Box>
                    
                    <Box w={{ base: "100%", md: "23%" }} h="60vh">
                      <FilePreview
                        fileUrl={proposal.verso_documento!}
                      />
                    </Box>
                    <Box
                      ml={{ base: 0, md: "5%" }}
                      w={{ base: "100%", md: "25%" }}
                    >
                      <PdfPreview
                        pdfUrl={proposal.evidencia!}
                        penultimatePage={false}
                      />
                    </Box>
                  </Flex>
                </Box>
                <Box>
                  <Flex
                    gap="2"
                    align="center"
                    flexDirection={{ base: "column", md: "row" }}
                  >
                    <Flex gap="2" align="center">
                      <Button
                        leftIcon={<FaTimes />}
                        size="sm"
                        {...tagButtonStyle("red")}
                        fontFamily="Lexend Deca"
                        fontWeight="normal"
                        px={2}
                        onClick={() => cancelProposal(proposal.id_proposta!)}
                      >
                        Rejeitar
                      </Button>
                      <Button
                        leftIcon={<FaBan />}
                        size="sm"
                        {...tagButtonStyle("gray")}
                        fontFamily="Lexend Deca"
                        fontWeight="normal"
                        px={2}
                        onClick={() => cancelProposal(proposal.id_proposta, true)}
                      >
                        Rejeitar e Black List
                      </Button>
                      <Button
                        leftIcon={<FaCheck />}
                        size="sm"
                        {...tagButtonStyle("green")}
                        fontFamily="Lexend Deca"
                        fontWeight="normal"
                        px={2}
                        onClick={() => approveProsal(proposal.id_proposta!)}
                      >
                        Aprovar
                      </Button>
                    </Flex>
                    <Flex
                      align="center"
                      gap="1"
                      w="40%"
                      flexDirection={{ base: "column", sm: "row" }}
                    >
                      <Flex
                        align="center"
                        gap="2"
                        px={3}
                        h="32px"
                        border="1px solid"
                        borderColor="teal.500"
                        borderRadius="md"
                        flexShrink={0}
                      >
                        <FormLabel
                          htmlFor={`status-${proposal.id_proposta}`}
                          m={0}
                          fontSize="sm"
                          fontWeight="normal"
                          fontFamily="Lexend Deca"
                          whiteSpace="nowrap"
                          cursor="pointer"
                        >
                          Mudar Status
                        </FormLabel>
                        <Switch
                          id={`status-${proposal.id_proposta}`}
                          size="sm"
                          colorScheme="teal"
                          isChecked={visibleInput === proposal.id_proposta}
                          onChange={() => handleSwitchChange(proposal.id_proposta!)}
                        />
                      </Flex>
                      {visibleInput === proposal.id_proposta && (
                        <Formik
                          initialValues={{
                            status: handleGetCustomStatus(proposal?.custom_status as string)

                          }}
                          validationSchema={validationSchema}
                          onSubmit={(values) => {
                            // console.log("test",values.status);
                            atualizarStatusProposta(proposal.id_proposta!, values.status);
                          }}
                        >
                          {({ values, setFieldValue, errors, touched }) => (
                            <Form style={{ width: "100%" }}>
                              <Flex gap="2" align="center">
                                <TextInputField
                                  name="status"
                                  placeholder="Digite novo status da proposta"
                                  value={values.status}
                                  setFieldValue={setFieldValue}
                                  error={errors.status}
                                  touched={touched.status}
                                />
                                <Button
                                  type="submit"
                                  size="sm"
                                  {...tagButtonStyle("green")}
                                  fontFamily="Lexend Deca"
                                  fontWeight="normal"
                                  px={3}
                                >
                                  Salvar
                                </Button>
                              </Flex>
                            </Form>
                          )}
                        </Formik>
                      )}
                    </Flex>
                    <Button
                      leftIcon={<FaFileAlt />}
                      size="sm"
                      {...tagButtonStyle("blue")}
                      fontFamily="Lexend Deca"
                      fontWeight="normal"
                      px={2}
                      ml={{ base: 0, md: "auto" }}
                      onClick={() => setScriptProposal(proposal)}
                    >
                      Script
                    </Button>
                  </Flex>
                </Box>
              </Stack>
            </CardBody>
          </Card>
        ))}
      </Flex>
      <CreditAuctionProposalsPagination
        currentPage={proposals?.pagina_atual ?? page}
        totalPages={proposals?.total_paginas ?? 1}
        pageSize={pageSize}
        totalItems={proposals?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
      <ScriptContratacaoModal
        proposal={scriptProposal}
        onClose={() => setScriptProposal(undefined)}
      />
    </Box >
  );
}
